// ============================================================
// Die Zeichenketten von „Aktuelles" (§16) — deutsch und englisch (E17).
//
// Eigene Datei aus demselben Grund wie bei F3 und F4: wer den Feed ändert,
// soll seine Sätze im selben Ordner finden und nicht in der Datei, die alle
// Bauenden gleichzeitig anfassen. Den SCHALTER stellt weiterhin die Schale
// (`lib/texte.ts`) — es gibt genau einen.
//
// `Woerter` ist der Vertrag zwischen beiden Sprachen: fehlt drüben ein Satz,
// fällt das beim Übersetzen auf und nicht im Bild.
//
// Die BEREICHSNAMEN standen bis §16.6 hier und stehen seither in der Datenbank
// (`bereich.name_de` / `name_en`, Wanderung 012) — genau wie die Phasennamen und
// aus demselben Grund: wer einen Bereich umbenennt, tut das in der Verwaltung,
// und eine zweite Liste im Programmtext liefe daneben her. Geblieben ist hier,
// was KEIN Datum ist: die Überschrift „Bereiche" und das „Alles" über allen.
// ============================================================
import type { Sprache } from "../../api/types";
import { useT } from "../../lib/texte";

export interface Woerter {
  titel: string;
  /** „12 Beiträge · 3 offene Fragen" */
  zaehler: (beitraege: number, offen: number) => string;
  feed: string;
  forum: string;
  neuerBeitrag: string;
  suchen: string;
  laedt: string;
  mehrLaden: string;
  fehler: string;
  schliessen: string;
  abbrechen: string;
  loeschen: string;
  bereiche: string;
  alleBereiche: string;
  projektfaeden: string;
  keineProjektfaeden: string;
  /** Der kleine Absatz unter der linken Spalte. */
  hinweisSpalte: string;

  /** Der Dreifachschalter auf dem Telefon. */
  mobilFeed: string;
  mobilForum: string;
  mobilBereiche: string;

  karte: {
    erfolg: string;
    abgabe: string;
    frage: string;
    news: string;
    wettbewerb: string;
    neuePerson: string;
    neuesProjekt: string;
    oeffentlich: string;
    oeffentlichTitel: string;
    kommentare: (n: number) => string;
    beantwortet: string;
    /** §51.8 f: die Marke an der Fadenzeile eines Rückblick-Beitrags, statt „beantwortet". */
    rueckblick: string;
    antworten: (n: number) => string;
    geloescht: string;
    reaktion: Record<"applaus" | "herz" | "pflanze", string>;
    /** §40.1: wer reagiert hat. */
    werHat: (art: string, namen: string) => string;
    weitere: (n: number) => string;
    werZeigen: string;
    werZu: string;
    pdf: string;
    bildWartet: string;
    bildFehler: string;
    ohneNachweis: string;
    bildZurueck: string;
    bildVor: string;
    /** §37.10: der Weg zur Sprechblase eines Beitrags mit Ort (`/karte?beitrag=<id>`). */
    aufKarte: string;
  };

  erfolge: {
    titel: string;
    keine: string;
  };

  newsletter: {
    titel: string;
    zeile: string;
    text: string;
    oeffnen: string;
  };

  leer: {
    feedTitel: string;
    feedText: string;
    forumTitel: string;
    forumText: string;
    sucheTitel: string;
    sucheText: string;
  };

  detail: {
    titel: string;
    kommentare: string;
    keineKommentare: string;
    schreiben: string;
    platzhalter: string;
    senden: string;
    zitieren: string;
    zitatWeg: string;
    /** Die Vorsilbe, die „Zitieren" in das Feld schreibt. */
    zitatVorsilbe: (autor: string, text: string) => string;
    alsAntwort: string;
    antwortWeg: string;
    kommentarLoeschen: string;
    beitragLoeschen: string;
    beitragLoeschenFrage: string;
    freigabe: string;
    freigabeHinweis: string;
    nachweis: string;
    nachweisPlatzhalter: string;
    nachweisFehlt: string;
    medienTitel: string;
    mediumLoeschen: string;
    gesichert: string;
  };

  neu: {
    titel: string;
    artNews: string;
    artFrage: string;
    artHinweis: string;
    feldTitel: string;
    feldTitelPlatzhalter: string;
    feldText: string;
    feldTextPlatzhalter: string;
    feldBereich: string;
    feldProjekt: string;
    keinProjekt: string;
    zielPflicht: string;
    titelPflicht: string;
    erfolgSchalter: string;
    erfolgHinweis: string;
    medien: string;
    medienAblage: string;
    nachweis: string;
    nachweisHinweis: string;
    /** Statt `nachweisHinweis`, wenn nur Videos anhängen (§53.3: sie brauchen nie einen Nachweis). */
    nachweisHinweisVideo: string;
    ausGalerie: string;
    ausGalerieTitel: string;
    ausGalerieLeer: string;
    /** §38.2: auf der Wahlkachel eines zugeschnittenen Galeriebilds — Beiträge nehmen die Datei, nicht den Ausschnitt. */
    ausGalerieGanz: string;
    ausGalerieOhneProjekt: string;
    uebernehmen: string;
    entfernen: string;
    senden: string;
    sendet: string;
    zuGross: (name: string) => string;
    medienFehler: string;
    fehler: string;
    /** §37.9: die Zeile über dem Titel, wenn der Beitrag aus „Beitrag mit Ort“ kommt. */
    mitOrt: string;
    ortEntfernen: string;
  };

  fragen: {
    titel: string;
    neu: string;
    leerTitel: string;
    leerText: string;
    offen: (n: number) => string;
    /** Fehlerrunde 01.10.2026, #31: an einer Einreichung gibt es noch keine Fragen (§36.4). */
    erstNachFreigabe: string;
  };

  feier: {
    abgabe: string;
    projekt: string;
    wettbewerb: string;
    /** §32.6 — die Feier eines privaten Abzeichens (Spielstufe „viel"). */
    abzeichen: string;
    schliessen: string;
  };

  schaufenster: {
    knopf: string;
    titel: string;
    untertitel: string;
    leer: string;
  };

  /** §53 Videos in Beiträgen (E53.5) — Composer, Spieler, Standbild. */
  video: {
    /** Knopf im Beitragsfenster (Autorin): ein weiteres Video an einen bestehenden Beitrag. */
    hinzufuegen: string;
    /** Unter der Ablage im Composer (E53.5, wörtlich). */
    hinweis: string;
    /** Während des Uploads im Fuß des Fensters (§53.5: auf dem Telefon sperrt sonst der Bildschirm, iOS wirft den Upload weg). */
    hinweisBleiben: string;
    zuGross: (name: string) => string;
    zuLang: (name: string) => string;
    zuViele: string;
    /** „42 %" — am Balken. */
    prozent: (n: number) => string;
    abbrechen: string;
    abgebrochen: string;
    /** An der Zeile bei 100 %: der Rumpf ist ganz gesendet, der Server legt ab — Abbrechen steht dann nicht mehr. */
    wirdAbgelegt: string;
    /** Rückfrage, wenn während eines Uploads weg-navigiert wird (Zurück-Geste, Link). */
    verlassenFrage: string;
    fehlgeschlagen: (name: string) => string;
    /** Zustand `wartet` am Spieler, im Composer nach dem Upload und in der Liste. */
    verarbeitet: string;
    verarbeitetHinweis: string;
    /** Zustand `fehler`, wenn der Server keinen Satz mitschickt. */
    fehler: string;
    abspielen: string;
    /** `aria-label` der Dauer-Pille: „Dauer 1:23". */
    dauer: (text: string) => string;
    original: string;
    entfernen: string;
    entfernenFrage: string;
    /** Wort am Anhang-Chip und am Standbild. */
    video: string;
    /** Inhalt im `<video>` für Browser ohne Abspieler. */
    keinAbspielen: string;
    /** Toast, wenn die ersten Bytes kein MP4/MOV/M4V/WebM sind (`videoKopfPasst`, §53.5) — der Satz von `medien.FEHLER_VIDEO_TYP`. */
    falscherTyp: (name: string) => string;
    /** An der Upload-Zeile, wenn `uploadSchaffbar` `false` sagt — neben „Upload abbrechen“ (§53.14). */
    zuLangsam: (minuten: number) => string;
    /** Statt „Keine Verbindung zum Server“, wenn ein Upload nach 500 s mit Status 0 endet (`uploadAbgerissen`). */
    abgerissen: string;
    /** Knopf an einer gescheiterten Upload-Zeile (`wiederholbar`): dieselbe Datei noch einmal. */
    wiederholen: string;
    /** Composer im Zustand „Beitrag steht, Video fehlt“: schließen, ohne es noch einmal zu versuchen. */
    ohneVideoSchliessen: string;
    /** Kopf über den gescheiterten Zeilen im Composer, nachdem der Beitrag schon steht. */
    videoFehlt: string;
    /** Freigabe-Karte im Beitragsfenster, wenn der Beitrag Videos trägt (§53.3). */
    nichtImSchaufenster: string;
  };
}

const de: Woerter = {
  titel: "Aktuelles",
  zaehler: (b, o) => `${b} ${b === 1 ? "Beitrag" : "Beiträge"} · ${o} offene ${o === 1 ? "Frage" : "Fragen"}`,
  feed: "Feed",
  forum: "Forum",
  neuerBeitrag: "Beitrag",
  suchen: "Im Aktuellen suchen",
  laedt: "Wird geladen",
  mehrLaden: "Mehr laden",
  fehler: "Das Aktuelle ließ sich nicht laden.",
  schliessen: "Schließen",
  abbrechen: "Abbrechen",
  loeschen: "Löschen",
  bereiche: "Bereiche",
  alleBereiche: "Alles",
  projektfaeden: "Projektfäden",
  keineProjektfaeden: "Keine offenen Fragen in deinen Projekten.",
  hinweisSpalte:
    "Reaktionen und Kommentare, keine Ranglisten. Öffentlich wird ein Beitrag erst nach Freigabe durch die Geschäftsführung.",

  mobilFeed: "Feed",
  mobilForum: "Forum",
  mobilBereiche: "Bereiche",

  karte: {
    erfolg: "Erfolg",
    abgabe: "Abgabe",
    frage: "Frage",
    news: "News",
    wettbewerb: "Wettbewerb",
    neuePerson: "Neu im Haus",
    neuesProjekt: "Neues Projekt",
    oeffentlich: "öffentlich",
    oeffentlichTitel: "Von der Geschäftsführung fürs Schaufenster freigegeben",
    kommentare: (n) => (n === 1 ? "1 Kommentar" : `${n} Kommentare`),
    beantwortet: "✓ beantwortet",
    rueckblick: "Rückblick",
    antworten: (n) => (n === 1 ? "1 Antwort" : `${n} Antworten`),
    geloescht: "[gelöscht]",
    reaktion: { applaus: "Applaus", herz: "Herz", pflanze: "Pflanze" },
    werHat: (art, namen) => `${art}: ${namen}`,
    weitere: (n) => (n === 1 ? "und 1 weitere Person" : `und ${n} weitere`),
    werZeigen: "Zeigen, wer reagiert hat",
    werZu: "Liste schließen",
    pdf: "PDF",
    bildWartet: "Wird aufbereitet",
    bildFehler: "Ableitung fehlgeschlagen",
    ohneNachweis: "Bildnachweis fehlt",
    bildZurueck: "Vorheriges Bild",
    bildVor: "Nächstes Bild",
    aufKarte: "Auf der Karte",
  },

  erfolge: {
    titel: "Erfolge dieser Woche",
    keine: "Diese Woche noch nichts — die Woche ist ja noch jung.",
  },

  newsletter: {
    titel: "Nächster Wochenbrief",
    zeile: "Montag, 08:00",
    text: "Setzt sich aus den Beiträgen, Erfolgen, neuen Projekten und Fotos der Woche zusammen.",
    oeffnen: "Wochenbrief",
  },

  leer: {
    feedTitel: "Noch nichts geschrieben.",
    feedText: "Ein Foto von der Baustelle, ein Satz zum Entwurf — dafür ist der Feed da.",
    forumTitel: "Noch keine Frage.",
    forumText: "Woran hängst du gerade? Frag das Haus, bevor du es zweimal löst.",
    sucheTitel: "Nichts gefunden.",
    sucheText: "Die Suche trifft Titel, Text und den Namen der schreibenden Person.",
  },

  detail: {
    titel: "Beitrag",
    kommentare: "Kommentare",
    keineKommentare: "Noch keine Antwort.",
    schreiben: "Antworten",
    platzhalter: "Antwort schreiben …",
    senden: "Senden",
    zitieren: "Zitieren",
    zitatWeg: "Zitat entfernen",
    zitatVorsilbe: (autor, text) => `> ${autor}: ${text}\n\n`,
    alsAntwort: "Als Antwort markieren",
    antwortWeg: "Markierung aufheben",
    kommentarLoeschen: "Kommentar löschen",
    beitragLoeschen: "Beitrag löschen",
    beitragLoeschenFrage: "Diesen Beitrag wirklich löschen?",
    freigabe: "Öffentlich",
    freigabeHinweis: "Im Schaufenster sichtbar — Name, Text und die Bilder mit Bildnachweis.",
    nachweis: "Bildnachweis (Pflicht für öffentlich)",
    nachweisPlatzhalter: "z. B. Foto: LILASp / S. Lang",
    nachweisFehlt: "Jedes Bild braucht einen Bildnachweis.",
    medienTitel: "Bilder und Pläne",
    mediumLoeschen: "Aus dem Beitrag nehmen",
    gesichert: "Gesichert.",
  },

  neu: {
    titel: "Neuer Beitrag",
    artNews: "News",
    artFrage: "Frage",
    artHinweis: "Eine Frage braucht einen Bereich oder ein Projekt — dort findet sie, wer sie beantworten kann.",
    feldTitel: "Titel",
    feldTitelPlatzhalter: "Worum geht es in einer Zeile?",
    feldText: "Text",
    feldTextPlatzhalter: "Was gibt es Neues? Foto, Plan oder ein Satz …",
    feldBereich: "Bereich",
    feldProjekt: "Projekt",
    keinProjekt: "— kein Projekt —",
    zielPflicht: "Wähle einen Bereich oder ein Projekt.",
    titelPflicht: "Ein Titel muss sein.",
    erfolgSchalter: "Wettbewerb oder Preis",
    erfolgHinweis: "Steht dann bei den Erfolgen der Woche und im Wochenbrief.",
    medien: "Bilder, Pläne und Videos",
    medienAblage: "Dateien hierher ziehen oder auswählen — Bilder, PDF und Videos.",
    nachweis: "Bildnachweis",
    nachweisHinweis: "Gilt für alle Anhänge dieses Beitrags. Ohne ihn lässt sich der Beitrag später nicht freigeben — nachtragen geht im Beitrag selbst.",
    nachweisHinweisVideo: "Wahlweise – Videos gehen nicht ins Schaufenster und brauchen keinen Nachweis.",
    ausGalerie: "Aus Projektgalerie wählen",
    ausGalerieTitel: "Bild aus der Projektgalerie",
    ausGalerieLeer: "Dieses Projekt hat noch keine Bilder.",
    ausGalerieGanz: "Beiträge zeigen das ganze Foto, ohne Zuschnitt.",
    ausGalerieOhneProjekt: "Wähle zuerst ein Projekt.",
    uebernehmen: "Übernehmen",
    entfernen: "Entfernen",
    senden: "Veröffentlichen",
    sendet: "Wird gesendet …",
    zuGross: (name) => `${name}: größer als 40 MB`,
    medienFehler: "Der Beitrag steht, aber nicht alle Bilder sind angekommen.",
    fehler: "Das hat nicht geklappt.",
    mitOrt: "Mit Ort auf der Karte",
    ortEntfernen: "Ort entfernen",
  },

  fragen: {
    titel: "Fragen",
    neu: "Frage stellen",
    leerTitel: "Noch keine Frage zu diesem Projekt.",
    leerText: "Fragen an diesem Projekt stehen auch im Forum — und umgekehrt.",
    offen: (n) => (n === 1 ? "1 offene Frage" : `${n} offene Fragen`),
    erstNachFreigabe: "Fragen zu diesem Projekt gibt es, sobald es freigegeben ist.",
  },

  feier: {
    abgabe: "Abgabe abgegeben",
    projekt: "Neues Projekt",
    wettbewerb: "Wettbewerb",
    abzeichen: "Abzeichen erreicht",
    schliessen: "Weiter",
  },

  schaufenster: {
    knopf: "Aktuelles",
    titel: "Aktuelles aus dem Büro",
    untertitel: "Was wir gerade bauen, planen und gewonnen haben.",
    leer: "Hier steht bald etwas.",
  },

  video: {
    hinzufuegen: "Video hinzufügen",
    hinweis: "Videos bis 500 MB, werden für alle Geräte umgewandelt.",
    hinweisBleiben: "Bildschirm anlassen und die App nicht wechseln, bis der Upload fertig ist.",
    zuGross: (name) => `${name}: größer als 500 MB`,
    zuLang: (name) => `${name}: länger als 15 Minuten – bitte kürzen`,
    zuViele: "Höchstens 3 Videos je Beitrag.",
    prozent: (n) => `${n}\u00a0%`,
    abbrechen: "Upload abbrechen",
    abgebrochen: "Upload abgebrochen.",
    wirdAbgelegt: "Hochgeladen – wird abgelegt …",
    verlassenFrage: "Ein Video lädt noch hoch. Wenn du jetzt gehst, bricht der Upload ab. Trotzdem gehen?",
    fehlgeschlagen: (name) => `${name} konnte nicht hochgeladen werden.`,
    verarbeitet: "Video wird verarbeitet …",
    verarbeitetHinweis: "Das dauert je nach Länge ein paar Minuten. Der Beitrag steht schon.",
    fehler: "Dieses Video ließ sich nicht umwandeln.",
    abspielen: "Video abspielen",
    dauer: (text) => `Dauer ${text}`,
    original: "Original herunterladen",
    entfernen: "Video entfernen",
    entfernenFrage: "Dieses Video aus dem Beitrag entfernen?",
    video: "Video",
    keinAbspielen: "Dieser Browser kann das Video nicht abspielen.",
    falscherTyp: (name) => `${name}: Als Video gehen MP4, MOV, M4V oder WebM.`,
    zuLangsam: (minuten) =>
      `Bei dieser Verbindung dauert der Upload etwa ${minuten} Minuten und bricht nach 10 ab – bitte im WLAN hochladen oder das Video kürzer oder kleiner exportieren.`,
    abgerissen:
      "Der Upload ist nach 10 Minuten abgebrochen – die Verbindung war zu langsam. Bitte im WLAN hochladen oder das Video kürzer oder kleiner exportieren.",
    wiederholen: "Erneut versuchen",
    ohneVideoSchliessen: "Ohne Video schließen",
    videoFehlt: "Der Beitrag steht – ein Video fehlt noch.",
    nichtImSchaufenster: "Videos erscheinen nicht im Schaufenster – dort stehen nur Text und Bilder.",
  },
};

const en: Woerter = {
  titel: "News",
  zaehler: (b, o) => `${b} ${b === 1 ? "post" : "posts"} · ${o} open ${o === 1 ? "question" : "questions"}`,
  feed: "Feed",
  forum: "Forum",
  neuerBeitrag: "Post",
  suchen: "Search the news",
  laedt: "Loading",
  mehrLaden: "Load more",
  fehler: "The news could not be loaded.",
  schliessen: "Close",
  abbrechen: "Cancel",
  loeschen: "Delete",
  bereiche: "Areas",
  alleBereiche: "Everything",
  projektfaeden: "Project threads",
  keineProjektfaeden: "No open questions in your projects.",
  hinweisSpalte: "Reactions and comments, no leaderboards. A post goes public only once the management releases it.",

  mobilFeed: "Feed",
  mobilForum: "Forum",
  mobilBereiche: "Areas",

  karte: {
    erfolg: "Success",
    abgabe: "Milestone",
    frage: "Question",
    news: "News",
    wettbewerb: "Competition",
    neuePerson: "New here",
    neuesProjekt: "New project",
    oeffentlich: "public",
    oeffentlichTitel: "Released by the management for the public showcase",
    kommentare: (n) => (n === 1 ? "1 comment" : `${n} comments`),
    beantwortet: "✓ answered",
    rueckblick: "Retrospective",
    antworten: (n) => (n === 1 ? "1 reply" : `${n} replies`),
    geloescht: "[deleted]",
    reaktion: { applaus: "Applause", herz: "Heart", pflanze: "Plant" },
    werHat: (art, namen) => `${art}: ${namen}`,
    weitere: (n) => (n === 1 ? "and 1 other" : `and ${n} others`),
    werZeigen: "Show who reacted",
    werZu: "Close list",
    pdf: "PDF",
    bildWartet: "Being processed",
    bildFehler: "Processing failed",
    ohneNachweis: "Credit missing",
    bildZurueck: "Previous image",
    bildVor: "Next image",
    aufKarte: "On the map",
  },

  erfolge: {
    titel: "This week's successes",
    keine: "Nothing yet this week — the week is still young.",
  },

  newsletter: {
    titel: "Next weekly letter",
    zeile: "Monday, 08:00",
    text: "Built from this week's posts, successes, new projects and photos.",
    oeffnen: "Weekly letter",
  },

  leer: {
    feedTitel: "Nothing written yet.",
    feedText: "A photo from site, a sentence about a design — that is what the feed is for.",
    forumTitel: "No question yet.",
    forumText: "What are you stuck on? Ask the office before solving it twice.",
    sucheTitel: "Nothing found.",
    sucheText: "The search covers title, text and the name of the person who wrote it.",
  },

  detail: {
    titel: "Post",
    kommentare: "Comments",
    keineKommentare: "No reply yet.",
    schreiben: "Reply",
    platzhalter: "Write a reply …",
    senden: "Send",
    zitieren: "Quote",
    zitatWeg: "Remove quote",
    zitatVorsilbe: (autor, text) => `> ${autor}: ${text}\n\n`,
    alsAntwort: "Mark as the answer",
    antwortWeg: "Remove the mark",
    kommentarLoeschen: "Delete comment",
    beitragLoeschen: "Delete post",
    beitragLoeschenFrage: "Really delete this post?",
    freigabe: "Public",
    freigabeHinweis: "Visible in the showcase — title, text and the images that carry a credit.",
    nachweis: "Image credit (required to go public)",
    nachweisPlatzhalter: "e.g. Photo: LILASp / S. Lang",
    nachweisFehlt: "Every image needs a credit.",
    medienTitel: "Images and plans",
    mediumLoeschen: "Remove from the post",
    gesichert: "Saved.",
  },

  neu: {
    titel: "New post",
    artNews: "News",
    artFrage: "Question",
    artHinweis: "A question needs an area or a project — that is where the people who can answer it look.",
    feldTitel: "Title",
    feldTitelPlatzhalter: "What is it about, in one line?",
    feldText: "Text",
    feldTextPlatzhalter: "What is new? A photo, a plan or a sentence …",
    feldBereich: "Area",
    feldProjekt: "Project",
    keinProjekt: "— no project —",
    zielPflicht: "Pick an area or a project.",
    titelPflicht: "A title is required.",
    erfolgSchalter: "Competition or prize",
    erfolgHinweis: "It then appears among this week's successes and in the weekly letter.",
    medien: "Images, plans and videos",
    medienAblage: "Drag files here or pick them — images, PDF and videos.",
    nachweis: "Image credit",
    nachweisHinweis: "Applies to every attachment of this post. Without it the post cannot be released later — you can add it in the post itself.",
    nachweisHinweisVideo: "Optional – videos do not go to the showcase and need no credit.",
    ausGalerie: "Pick from the project gallery",
    ausGalerieTitel: "Image from the project gallery",
    ausGalerieLeer: "This project has no images yet.",
    ausGalerieGanz: "Posts show the whole photo, without the crop.",
    ausGalerieOhneProjekt: "Pick a project first.",
    uebernehmen: "Use",
    entfernen: "Remove",
    senden: "Publish",
    sendet: "Sending …",
    zuGross: (name) => `${name}: larger than 40 MB`,
    medienFehler: "The post is up, but not every image arrived.",
    fehler: "That did not work.",
    mitOrt: "With location on the map",
    ortEntfernen: "Remove location",
  },

  fragen: {
    titel: "Questions",
    neu: "Ask a question",
    leerTitel: "No question about this project yet.",
    leerText: "Questions on a project also show up in the forum — and the other way round.",
    offen: (n) => (n === 1 ? "1 open question" : `${n} open questions`),
    erstNachFreigabe: "Questions about this project open up once it has been approved.",
  },

  feier: {
    abgabe: "Milestone handed in",
    projekt: "New project",
    wettbewerb: "Competition",
    abzeichen: "Badge earned",
    schliessen: "Go on",
  },

  schaufenster: {
    knopf: "News",
    titel: "News from the office",
    untertitel: "What we are building, planning and have won.",
    leer: "Something will show up here soon.",
  },

  video: {
    hinzufuegen: "Add video",
    hinweis: "Videos up to 500 MB, converted to play on every device.",
    hinweisBleiben: "Keep the screen on and stay in this app until the upload has finished.",
    zuGross: (name) => `${name}: larger than 500 MB`,
    zuLang: (name) => `${name}: longer than 15 minutes – please trim it`,
    zuViele: "At most 3 videos per post.",
    prozent: (n) => `${n}%`,
    abbrechen: "Cancel upload",
    abgebrochen: "Upload cancelled.",
    wirdAbgelegt: "Uploaded – saving …",
    verlassenFrage: "A video is still uploading. Leaving now cancels the upload. Leave anyway?",
    fehlgeschlagen: (name) => `${name} could not be uploaded.`,
    verarbeitet: "Processing video …",
    verarbeitetHinweis: "Depending on its length this takes a few minutes. The post is already up.",
    fehler: "This video could not be converted.",
    abspielen: "Play video",
    dauer: (text) => `Duration ${text}`,
    original: "Download original",
    entfernen: "Remove video",
    entfernenFrage: "Remove this video from the post?",
    video: "Video",
    keinAbspielen: "This browser cannot play the video.",
    falscherTyp: (name) => `${name}: videos must be MP4, MOV, M4V or WebM.`,
    zuLangsam: (minuten) =>
      `On this connection the upload would take about ${minuten} minutes and stops after 10 – please upload over Wi-Fi or export the video shorter or smaller.`,
    abgerissen:
      "The upload stopped after 10 minutes – the connection was too slow. Please upload over Wi-Fi or export the video shorter or smaller.",
    wiederholen: "Try again",
    ohneVideoSchliessen: "Close without the video",
    videoFehlt: "The post is up – a video is still missing.",
    nichtImSchaufenster: "Videos do not appear in the showcase – it shows only text and images.",
  },
};

const WOERTER: Record<Sprache, Woerter> = { de, en };

/** Die Wörter dieses Bildschirms in der eingestellten Sprache, dazu die Sprache
 *  selbst — Datumsformate brauchen sie. */
export function useTexte(): Woerter & { sprache: Sprache } {
  const { sprache } = useT();
  return { ...WOERTER[sprache], sprache };
}
